import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronUp, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Card } from '@/components/ui/card';

interface CollapsibleAdvancedSearchProps {
  className?: string;
}

export const CollapsibleAdvancedSearch: React.FC<CollapsibleAdvancedSearchProps> = ({ className }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);
  
  const [filters, setFilters] = useState({
    salaryRange: [30000, 150000],
    noDegreeOnly: searchParams.get('no_degree') === 'true',
    employmentLevel: searchParams.get('employment') || 'any'
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(value);
  };

  const handleSearch = () => {
    const currentQuery = searchParams.get('query') || '';
    const newSearchParams = new URLSearchParams();
    
    if (currentQuery) {
      newSearchParams.set('query', currentQuery);
    }
    
    newSearchParams.set('min_salary', filters.salaryRange[0].toString());
    newSearchParams.set('max_salary', filters.salaryRange[1].toString());
    
    if (filters.noDegreeOnly) {
      newSearchParams.set('no_degree', 'true');
    }
    
    if (filters.employmentLevel && filters.employmentLevel !== 'any') {
      newSearchParams.set('employment', filters.employmentLevel);
    }

    navigate(`/search?${newSearchParams.toString()}`);
    setIsOpen(false);
  };

  const resetFilters = () => {
    setFilters({
      salaryRange: [30000, 150000],
      noDegreeOnly: false,
      employmentLevel: 'any'
    });
  };

  const hasActiveFilters = filters.noDegreeOnly || 
    filters.employmentLevel !== 'any' || 
    filters.salaryRange[0] !== 30000 || 
    filters.salaryRange[1] !== 150000;

  return (
    <div className={className}>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <Button variant="outline" className="w-full justify-between">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Advanced Filters
              {hasActiveFilters && (
                <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full">
                  Active
                </span>
              )}
            </div>
            {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Card className="mt-4 p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Salary Range */}
              <div className="space-y-3">
                <label className="text-sm font-medium">
                  Annual Salary Range
                </label>
                <div className="px-3">
                  <Slider
                    value={filters.salaryRange}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, salaryRange: value }))}
                    max={300000}
                    min={20000}
                    step={5000}
                    className="w-full"
                  />
                </div>
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>{formatCurrency(filters.salaryRange[0])}</span>
                  <span>{formatCurrency(filters.salaryRange[1])}</span>
                </div>
              </div>

              {/* Education Requirements */}
              <div className="space-y-3">
                <label className="text-sm font-medium">
                  Education Requirements
                </label>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="no-degree"
                    checked={filters.noDegreeOnly}
                    onCheckedChange={(checked) => 
                      setFilters(prev => ({ ...prev, noDegreeOnly: !!checked }))
                    }
                  />
                  <label
                    htmlFor="no-degree"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    No degree required only
                  </label>
                </div>
              </div>

              {/* Employment Level */}
              <div className="space-y-3">
                <label className="text-sm font-medium">
                  Employment Level
                </label>
                <Select 
                  value={filters.employmentLevel} 
                  onValueChange={(value) => setFilters(prev => ({ ...prev, employmentLevel: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Any employment level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any employment level</SelectItem>
                    <SelectItem value="high">High employment (50,000+ jobs)</SelectItem>
                    <SelectItem value="medium">Medium employment (10,000-50,000 jobs)</SelectItem>
                    <SelectItem value="low">Low employment (Under 10,000 jobs)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <Button variant="outline" onClick={resetFilters}>
                Reset
              </Button>
              <Button onClick={handleSearch}>
                Apply Filters
              </Button>
            </div>
          </Card>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};